/**
 * ShareSessionModal Component
 *
 * Modal for managing chat session sharing.
 * Allows users to share sessions with other users or groups,
 * view current shares, and modify access levels.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Modal } from '@/design-system/molecules/Modal'
import { Button } from '@/design-system/atoms/Button'
import { Spinner } from '@/design-system/atoms/Spinner'
import { Icon } from '@/design-system/atoms/Icon'
import { sharingApi } from '@/services/sharingService'
import type {
  ChatSession,
  ChatSessionShare,
  EligibleShareTargets,
  ShareAccessLevel,
  UserMinimal,
  GroupMinimal,
} from '@/types/agents'

interface ShareSessionModalProps {
  isOpen: boolean
  onClose: () => void
  session: ChatSession | null
  onSharesUpdated?: () => void
}

type TabType = 'share' | 'participants'

export const ShareSessionModal: React.FC<ShareSessionModalProps> = ({
  isOpen,
  onClose,
  session,
  onSharesUpdated,
}) => {
  // State
  const [activeTab, setActiveTab] = useState<TabType>('share')
  const [shares, setShares] = useState<ChatSessionShare[]>([])
  const [eligibleTargets, setEligibleTargets] = useState<EligibleShareTargets>({
    users: [],
    groups: [],
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savingShare, setSavingShare] = useState(false)

  // Transfer ownership state
  const [showTransferConfirm, setShowTransferConfirm] = useState(false)
  const [transferTargetUser, setTransferTargetUser] =
    useState<UserMinimal | null>(null)
  const [transferring, setTransferring] = useState(false)

  // Form state
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [selectedGroups, setSelectedGroups] = useState<number[]>([])
  const [accessLevel, setAccessLevel] =
    useState<ShareAccessLevel>('participate')
  const [searchQuery, setSearchQuery] = useState('')

  // Load shares and eligible targets when modal opens
  const loadData = useCallback(async () => {
    if (!session?.id) return

    setLoading(true)
    setError(null)

    try {
      const [sharesData, targetsData] = await Promise.all([
        sharingApi.getShares(session.id),
        sharingApi.getEligibleTargets(session.id),
      ])
      setShares(sharesData)
      setEligibleTargets(targetsData)
    } catch {
      setError('Erro ao carregar dados de compartilhamento')
    } finally {
      setLoading(false)
    }
  }, [session?.id])

  useEffect(() => {
    if (isOpen && session?.id) {
      loadData()
      // Reset form state
      setSelectedUsers([])
      setSelectedGroups([])
      setAccessLevel('view')
      setSearchQuery('')
    }
  }, [isOpen, session?.id, loadData])

  // Handle creating new shares
  const handleCreateShares = async () => {
    if (!session?.id) return
    if (selectedUsers.length === 0 && selectedGroups.length === 0) {
      setError('Selecione pelo menos um usuário ou grupo')
      return
    }

    setSavingShare(true)
    setError(null)

    try {
      await sharingApi.createShares(session.id, {
        users: selectedUsers,
        groups: selectedGroups,
        accessLevel,
      })
      // Reset form and reload
      setSelectedUsers([])
      setSelectedGroups([])
      await loadData()
      onSharesUpdated?.()
    } catch {
      setError('Erro ao criar compartilhamento')
    } finally {
      setSavingShare(false)
    }
  }

  // Handle updating a share's access level
  const handleUpdateShare = async (
    shareId: string,
    newLevel: ShareAccessLevel
  ) => {
    if (!session?.id) return

    try {
      await sharingApi.updateShare(session.id, shareId, newLevel)
      setShares(prev =>
        prev.map(s => (s.id === shareId ? { ...s, accessLevel: newLevel } : s))
      )
      onSharesUpdated?.()
    } catch {
      setError('Erro ao atualizar compartilhamento')
    }
  }

  // Handle deleting a share
  const handleDeleteShare = async (shareId: string) => {
    if (!session?.id) return

    try {
      await sharingApi.deleteShare(session.id, shareId)
      setShares(prev => prev.filter(s => s.id !== shareId))
      await loadData() // Reload to update eligible targets
      onSharesUpdated?.()
    } catch {
      setError('Erro ao remover compartilhamento')
    }
  }

  // Handle transfer ownership
  const handleTransferOwnership = async () => {
    if (!session?.id || !transferTargetUser) return

    setTransferring(true)
    setError(null)

    try {
      await sharingApi.transferOwnership(session.id, transferTargetUser.id)
      setShowTransferConfirm(false)
      setTransferTargetUser(null)
      onSharesUpdated?.()
      onClose() // Close modal after transfer
    } catch {
      setError('Erro ao transferir propriedade')
    } finally {
      setTransferring(false)
    }
  }

  // Start transfer process
  const initiateTransfer = (user: UserMinimal) => {
    setTransferTargetUser(user)
    setShowTransferConfirm(true)
  }

  // Toggle user selection
  const toggleUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  // Toggle group selection
  const toggleGroup = (groupId: number) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }

  // Filter targets by search query
  const filteredUsers = eligibleTargets.users.filter(
    user =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredGroups = eligibleTargets.groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (!session) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" showCloseButton={false}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Compartilhar Conversa
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {session.title}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            onClick={() => setActiveTab('share')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'share'
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Compartilhar
          </button>
          <button
            onClick={() => setActiveTab('participants')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'participants'
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Participantes ({shares.length + 1})
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* Share Tab */}
            {activeTab === 'share' && (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar usuários ou grupos..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pl-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:text-white"
                  />
                  <Icon
                    name="search"
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </div>

                {/* Access level selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nível de Acesso
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAccessLevel('view')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        accessLevel === 'view'
                          ? 'bg-brand-50 border-brand-500 text-brand-700 dark:bg-brand-900/30 dark:border-brand-500 dark:text-brand-400'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <span className="block font-medium">Visualizar</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        Apenas ler mensagens
                      </span>
                    </button>
                    <button
                      onClick={() => setAccessLevel('participate')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        accessLevel === 'participate'
                          ? 'bg-brand-50 border-brand-500 text-brand-700 dark:bg-brand-900/30 dark:border-brand-500 dark:text-brand-400'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <span className="block font-medium">Participar</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        Enviar mensagens
                      </span>
                    </button>
                  </div>
                </div>

                {/* Users list */}
                {filteredUsers.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Usuários
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                      {filteredUsers.map(user => (
                        <UserSelectItem
                          key={user.id}
                          user={user}
                          selected={selectedUsers.includes(user.id)}
                          onToggle={() => toggleUser(user.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Groups list */}
                {filteredGroups.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Grupos
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                      {filteredGroups.map(group => (
                        <GroupSelectItem
                          key={group.id}
                          group={group}
                          selected={selectedGroups.includes(group.id)}
                          onToggle={() => toggleGroup(group.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* No results */}
                {filteredUsers.length === 0 && filteredGroups.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Icon
                      name="users"
                      size={48}
                      strokeWidth={1.5}
                      className="mx-auto mb-3 text-gray-300 dark:text-gray-600"
                    />
                    <p className="text-sm">
                      Nenhum usuário ou grupo disponível
                    </p>
                  </div>
                )}

                {/* Share button */}
                <Button
                  variant="primary"
                  fullWidth
                  disabled={
                    selectedUsers.length === 0 && selectedGroups.length === 0
                  }
                  loading={savingShare}
                  onClick={handleCreateShares}
                >
                  Compartilhar com{' '}
                  {selectedUsers.length + selectedGroups.length}{' '}
                  {selectedUsers.length + selectedGroups.length === 1
                    ? 'selecionado'
                    : 'selecionados'}
                </Button>
              </div>
            )}

            {/* Participants Tab */}
            {activeTab === 'participants' && (
              <div className="space-y-3">
                {/* Transfer ownership confirmation */}
                {showTransferConfirm && transferTargetUser && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                      Transferir propriedade?
                    </h4>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                      Você está prestes a transferir a propriedade desta
                      conversa para{' '}
                      <strong>
                        {transferTargetUser.fullName ||
                          transferTargetUser.username}
                      </strong>
                      . Você perderá o controle de gerenciamento e se tornará um
                      participante.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        loading={transferring}
                        onClick={handleTransferOwnership}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        Confirmar transferência
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setShowTransferConfirm(false)
                          setTransferTargetUser(null)
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Owner */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center text-brand-600 dark:text-brand-400 font-medium text-sm">
                      {session.isOwner ? 'Eu' : 'O'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {session.isOwner ? 'Você' : 'Proprietário'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Proprietário
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/30 px-2 py-1 rounded">
                    Owner
                  </span>
                </div>

                {/* Shared users/groups */}
                {shares.map(share => (
                  <ShareListItem
                    key={share.id}
                    share={share}
                    canManage={session.isOwner ?? false}
                    onUpdateLevel={newLevel =>
                      handleUpdateShare(share.id, newLevel)
                    }
                    onDelete={() => handleDeleteShare(share.id)}
                    onTransfer={
                      share.sharedWithUser
                        ? () => initiateTransfer(share.sharedWithUser!)
                        : undefined
                    }
                  />
                ))}

                {shares.length === 0 && (
                  <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                    <p className="text-sm">Nenhum compartilhamento ativo</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// Sub-components

interface UserSelectItemProps {
  user: UserMinimal
  selected: boolean
  onToggle: () => void
}

const UserSelectItem: React.FC<UserSelectItemProps> = ({
  user,
  selected,
  onToggle,
}) => (
  <button
    onClick={onToggle}
    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
      selected
        ? 'bg-brand-50 dark:bg-brand-900/30'
        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
    }`}
  >
    <div
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
        selected
          ? 'bg-brand-500 border-brand-500'
          : 'border-gray-300 dark:border-gray-600'
      }`}
    >
      {selected && <Icon name="check" size={12} className="text-white" />}
    </div>
    <div className="flex-1 text-left">
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        {user.fullName || user.username}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
    </div>
  </button>
)

interface GroupSelectItemProps {
  group: GroupMinimal
  selected: boolean
  onToggle: () => void
}

const GroupSelectItem: React.FC<GroupSelectItemProps> = ({
  group,
  selected,
  onToggle,
}) => (
  <button
    onClick={onToggle}
    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
      selected
        ? 'bg-brand-50 dark:bg-brand-900/30'
        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
    }`}
  >
    <div
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
        selected
          ? 'bg-brand-500 border-brand-500'
          : 'border-gray-300 dark:border-gray-600'
      }`}
    >
      {selected && <Icon name="check" size={12} className="text-white" />}
    </div>
    <div className="flex items-center gap-2">
      <Icon name="users" size={16} className="text-gray-400" />
      <span className="text-sm font-medium text-gray-900 dark:text-white">
        {group.name}
      </span>
    </div>
  </button>
)

interface ShareListItemProps {
  share: ChatSessionShare
  canManage: boolean
  onUpdateLevel: (level: ShareAccessLevel) => void
  onDelete: () => void
  onTransfer?: () => void
}

const ShareListItem: React.FC<ShareListItemProps> = ({
  share,
  canManage,
  onUpdateLevel,
  onDelete,
  onTransfer,
}) => {
  const [showMenu, setShowMenu] = useState(false)

  const icon =
    share.targetType === 'group' ? (
      <Icon name="users" size={16} className="text-gray-500" />
    ) : (
      <Icon name="user" size={16} className="text-gray-500" />
    )

  const accessLabel =
    share.accessLevel === 'participate' ? 'Participar' : 'Visualizar'

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg group">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {share.targetName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {share.targetType === 'group' ? 'Grupo' : 'Usuário'}
          </p>
        </div>
      </div>

      {canManage ? (
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            {accessLabel}
            <Icon name="chevron-down" size={12} className="ml-1 inline-block" />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
              <button
                onClick={() => {
                  onUpdateLevel('view')
                  setShowMenu(false)
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  share.accessLevel === 'view'
                    ? 'text-brand-600 dark:text-brand-400'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                Visualizar
              </button>
              <button
                onClick={() => {
                  onUpdateLevel('participate')
                  setShowMenu(false)
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  share.accessLevel === 'participate'
                    ? 'text-brand-600 dark:text-brand-400'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                Participar
              </button>
              <hr className="border-gray-200 dark:border-gray-700" />
              {onTransfer && (
                <button
                  onClick={() => {
                    onTransfer()
                    setShowMenu(false)
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  Transferir propriedade
                </button>
              )}
              <button
                onClick={() => {
                  onDelete()
                  setShowMenu(false)
                }}
                className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Remover acesso
              </button>
            </div>
          )}
        </div>
      ) : (
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1">
          {accessLabel}
        </span>
      )}
    </div>
  )
}

export default ShareSessionModal
